import { Button, Text } from "@chakra-ui/react";
import { LuPlus } from "react-icons/lu";
import { PageContainer } from "@/ui/containers/PageContainer";
import { useGetAliasProducts } from "../useCases/useGetAliasProducts";
import { useEffect, useState } from "react";
import { notify } from "@/ui/chakra/notify";
import { useConfirmToggle } from "@/ui/modals/useConfirm";
import { CreateAliasProductModal } from "../components/CreateAliasProductModal";
import { Table, TableProps } from "@/ui/tables/Table";
import { getStoredPageSize } from "@/ui/tables/pageSize";
import { AliasProduct } from "../types/AliasProduct";
import { EditAliasProductModal } from "../components/EditAliasProductModal";
import ModalProductDetails from "@/core/products/components/ModalProductDetails";
import { useUpdateAliasProduct } from "../useCases/useUpdateAliasProduct";
import { Pagination } from "@/core/common/types";
import { Product } from "@/shared/core-products";
import { formatDate } from "@/utils/formatDate";
import { useDebounce } from "@/core/common/hooks/useDebounce";
import { useAuth } from "@/shared/session";

const Columns = (
  onSelectProduct: (product: Product) => void,
  onChangeState: (aliasProduct: AliasProduct) => void,
): TableProps<AliasProduct>["headers"] => [
  {
    label: "Nombre",
    itemLabel: (item) => <Text fontWeight="semibold">{item.name}</Text>,
  },
  {
    label: "Descripción",
    itemLabel: "description",
  },
  {
    label: "Producto",
    itemLabel: (item) => (
      <Button
        size="sm"
        variant="outline"
        onClick={() => onSelectProduct(item.product)}
      >
        {item.product?.description ||
          item.product?.externalProduct?.code ||
          item.product.erpProduct?.erpCode}
      </Button>
    ),
  },
  {
    label: "Fecha de creación",
    itemLabel: (item) => formatDate(item.createdAt),
  },
  {
    label: "Estado",
    itemLabel: (item) => (
      <Button
        variant="ghost"
        size="sm"
        colorPalette={item.enabled ? "gray" : "green"}
        color={item.enabled ? "fg.muted" : undefined}
        onClick={() => onChangeState(item)}
      >
        {item.enabled ? "Deshabilitar" : "Habilitar"}
      </Button>
    ),
  },
];

export const AliasProductPage = () => {
  const { session } = useAuth();
  const [showDisabled, setShowDisabled] = useState(false);

  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    pageSize: getStoredPageSize(),
  });

  const [searchTerm, setSearchTerm] = useState<string>();
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const {
    data,
    isPending,
    pagination: metaAliasProducts,
  } = useGetAliasProducts({
    page: pagination.page,
    pageSize: pagination.pageSize,
    showDisabled: showDisabled,
    searchTerm: debouncedSearchTerm,
    zoneIds: session?.zone ?? undefined,
  });
  const { mutate } = useUpdateAliasProduct();

  const [isOpenCreateModal, setIsOpenCreateModal] = useState(false);
  const [isOpenEditModal, setIsOpenEditModal] = useState(false);

  const [selectedProductDetails, setSelectedProductDetails] =
    useState<Product | null>(null);

  const [selectedAliasProduct, setSelectedAliasProduct] =
    useState<AliasProduct | null>(null);

  const confirmToggle = useConfirmToggle();

  const handleToggleEnabled = async (aliasProduct: AliasProduct) => {
    const ok = await confirmToggle(
      !!aliasProduct.enabled,
      `el alias "${aliasProduct.name}"`,
    );
    if (!ok) return;
    const updatedAliasProduct = {
      ...aliasProduct,
      enabled: !aliasProduct.enabled,
    };
    mutate(
      {
        id: aliasProduct.id,
        updateAliasProducts: updatedAliasProduct,
      },
      {
        onSuccess: () =>
          notify.success(
            aliasProduct.enabled ? "Alias deshabilitado" : "Alias habilitado",
          ),
        onError: (error) =>
          notify.error("No se pudo actualizar el alias", error),
      },
    );
  };

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [debouncedSearchTerm]);
  return (
    <PageContainer
      display={"flex"}
      flexDir={"column"}
      gap={4}
      title="Alias de productos"
      actions={
        <Button colorPalette="brand" onClick={() => setIsOpenCreateModal(true)}>
          <LuPlus />
          Crear alias de producto
        </Button>
      }
    >
      <Table
        searchPlaceholder="Buscar por alias o descripción…"
        pageSize={pagination.pageSize}
        onChangePageSize={(pageSize) => setPagination({ page: 1, pageSize })}
        isLoading={isPending}
        emptyDescription="Un alias permite buscar un producto por otro código o nombre."
        emptyAction={
          <Button
            colorPalette="brand"
            size="sm"
            onClick={() => setIsOpenCreateModal(true)}
          >
            Crear alias de producto
          </Button>
        }
        data={data}
        extractItemKey={(item) => item.id}
        headers={Columns(setSelectedProductDetails, handleToggleEnabled)}
        pages={metaAliasProducts?.totalPages}
        totalItems={metaAliasProducts?.count}
        itemsLabel="alias"
        onEdit={(item) => {
          setSelectedAliasProduct(item);
          setIsOpenEditModal(true);
        }}
        onSearch={(value) => setSearchTerm(value)}
        showDisabledCheck
        onShowDisabledChange={(value) => setShowDisabled(value)}
        onChangePage={(page) =>
          setPagination({ ...pagination, page: page + 1 })
        }
        hiddenColumnsKey="aliasProducts"
      />
      {selectedAliasProduct && (
        <EditAliasProductModal
          isOpen={isOpenEditModal}
          onClose={() => setIsOpenEditModal(false)}
          selectedAliasProduct={selectedAliasProduct}
        />
      )}
      {selectedProductDetails && (
        <ModalProductDetails
          isOpen={!!selectedProductDetails}
          onClose={() => setSelectedProductDetails(null)}
          selectedProduct={selectedProductDetails}
        />
      )}

      <CreateAliasProductModal
        isOpen={isOpenCreateModal}
        onClose={() => setIsOpenCreateModal(false)}
      />
    </PageContainer>
  );
};
